import React, { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import {
  DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_FAILED,
  DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_SUCCESS,
} from 'ducks/drawers/ids'
import {
  DRAWER_TYPE_CHANNELS_AUTHENTICATE_FAILED,
  DRAWER_TYPE_CHANNELS_AUTHENTICATE_SUCCESS,
} from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import {
  selectPendingChannelById,
  selectCurrentChannelById,
} from 'ducks/channels/selectors'
import {
  doAddGmailChannel,
  doOauthGmailChannel,
  doAddMessengerChannel,
} from 'ducks/channels/actions'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import { oAuthErrorToErrorCode } from 'subapps/settings/components/drawers/Channels/ChannelDetails/oAuthError'
import { doGmailImport } from 'ducks/mailboxes/actions'
import { CHANNEL_TYPES, drawerConfig } from '../Channels.data'
import useSourceWording from './useSourceWording'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const drawerSuccessConfig = {
  id: DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_SUCCESS,
  type: DRAWER_TYPE_CHANNELS_AUTHENTICATE_SUCCESS,
  closeIgnoresStack: true,
}

const drawerFailedConfig = {
  id: DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_FAILED,
  type: DRAWER_TYPE_CHANNELS_AUTHENTICATE_FAILED,
  closeIgnoresStack: true,
}

const AuthenticateChannel = ({
  onClose,
  onExit,
  drawerResourceId: channelId,
  drawerChannelType: channelType,
  drawerRetryAuth: retryAuth,
  drawerSource: source = 'settings',
  previousDrawer,
}) => {
  const dispatch = useDispatch()
  const pendingChannel = useSelector(state =>
    selectPendingChannelById(state, channelId)
  )
  const currentChannel = useSelector(state =>
    selectCurrentChannelById(state, channelId)
  )

  const [nextDrawerConfig, setNextDrawerConfig] = useState(drawerSuccessConfig)
  const [createdMailbox, setCreatedMailbox] = useState(null)
  const [errorCode, setErrorCode] = useState(null)

  const channel = pendingChannel || currentChannel
  const { email: channelEmail } = channel || {}
  const channelTypeConfig = CHANNEL_TYPES[channelType] || {}
  const { name } = channelTypeConfig
  const channelTitle = channelEmail || name
  const {
    drawerTitle: sourceDrawerTitle,
    title: sourceTitle,
    intro: sourceIntro,
  } = useSourceWording(source)

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    nextDrawerConfig
  )

  const handleError = useCallback(
    error => {
      setErrorCode(oAuthErrorToErrorCode(error, channelType))
      // eslint-disable-next-line no-console
      console.error(error)

      setNextDrawerConfig(drawerFailedConfig)
      setCreatedMailbox(false)
    },
    [channelType]
  )

  const handleGmailChannel = useCallback(
    async () => {
      try {
        setCreatedMailbox(null)
        setErrorCode(null)
        let mailbox = null
        if (source === 'inbox') {
          mailbox = await dispatch(doOauthGmailChannel(channelId))
          await dispatch(
            doGmailImport(channelId, {
              import_history: true,
              import_labels: false,
              close_tickets: false,
              pages_to_import: 2,
            })
          )
        } else {
          mailbox = await dispatch(doAddGmailChannel(pendingChannel))
        }
        setNextDrawerConfig(drawerSuccessConfig)
        setCreatedMailbox(mailbox)
      } catch (error) {
        handleError(error)
      }
    },
    [dispatch, pendingChannel, handleError, source, channelId]
  )

  const handleMessengerChannel = useCallback(
    async () => {
      try {
        setCreatedMailbox(null)
        setErrorCode(null)
        const widget = await dispatch(doAddMessengerChannel(pendingChannel))
        setNextDrawerConfig(drawerSuccessConfig)
        setCreatedMailbox(widget)
      } catch (error) {
        handleError(error)
      }
    },
    [dispatch, pendingChannel, handleError]
  )

  const handleOnClick = useCallback(
    () => {
      if (channelType === 'google') {
        handleGmailChannel()
      } else if (channelType === 'messenger') {
        handleMessengerChannel()
      }
    },
    [channelType, handleGmailChannel, handleMessengerChannel]
  )

  useEffect(
    () => {
      if (createdMailbox === null) return
      const query = {
        ...buildDrawerQueryParam(
          nextDrawerId,
          'drawerChannelType',
          channelType
        ),
      }
      if (errorCode) {
        Object.assign(
          query,
          buildDrawerQueryParam(nextDrawerId, 'drawerAuthErrorCode', errorCode)
        )
      }
      if (source) {
        Object.assign(
          query,
          buildDrawerQueryParam(nextDrawerId, 'drawerSource', source)
        )
      }
      openNextDrawer(createdMailbox?.id, {
        clearStack: !!createdMailbox,
        query,
      })
    },
    // We only want to open the net drawer when the createdMailbox changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openNextDrawer, createdMailbox]
  )

  useEffect(
    () => {
      if (retryAuth === 1) {
        handleOnClick()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [retryAuth]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const footer = (
    <ModalBtns
      saveBtnText={`Authenticate ${name}`}
      onSave={handleOnClick}
      tertiaryBtnText={previousDrawer && 'Back'}
      onClickTertiaryBtn={handleBack}
    />
  )

  return (
    <AdminAccessDrawer
      title={sourceDrawerTitle || DEFAULT_DRAWER_TITLE}
      onClose={onExit}
      footer={footer}
    >
      <HeaderWithImage
        className="grui mt-14 mb-12"
        title={sourceTitle || `Authenticate ${name}`}
        intro={
          sourceIntro ||
          `To start receiving your ${channelTitle} conversations in Groove, you will need to authenticate your ${name} account.`
        }
      />
    </AdminAccessDrawer>
  )
}

export default AuthenticateChannel
